import React, { useState, useEffect } from 'react';
import { Modal, Table, Tag, Empty, Spin } from 'antd';
import { Consignment, ConsignmentHistory } from '../../model/consignment';
import { api_consignment } from '../../api/consignment';
import { formatActor, formatEventTime, hasRealActor } from '../../utils/audit-format';

interface Props {
    open: boolean;
    item: Consignment | null;
    onClose: () => void;
}

export const ConsignmentHistoryModal: React.FC<Props> = ({
    open,
    item,
    onClose
}) => {
    const [histories, setHistories] = useState<ConsignmentHistory[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && item) {
            loadHistory(item.id);
        }
    }, [open, item]);

    const loadHistory = async (id: number) => {
        setLoading(true);
        try {
            const res = await api_consignment('HISTORY', {}, id);
            setHistories(Array.isArray(res) ? res : []);
        } catch (e) {
            console.error('Failed to fetch history', e);
        } finally {
            setLoading(false);
        }
    };

    if (!item) return null;

    const periodNoById = new Map((item.periods || []).map(p => [p.id, p.period_no]));

    const columns = [
        {
            title: 'งวด',
            dataIndex: 'period_id',
            key: 'period_id',
            width: 70,
            align: 'center' as const,
            render: (periodId: number | undefined) => periodId && periodNoById.has(periodId)
                ? <Tag>#{periodNoById.get(periodId)}</Tag>
                : <span className="consignment-cell-sub">-</span>
        },
        {
            title: 'วัน-เวลา',
            dataIndex: 'created_at_iso',
            key: 'created_at',
            width: 140,
            // Phase 7C-6C: `created_at_iso` is the Plan B field -- the same
            // clock value as before, but carrying its `+07:00` meaning, so
            // it can be rendered as a date instead of shown as a raw
            // string. `created_at` is the legacy field and stays as the
            // fallback until every deployed Backend sends the new one;
            // `formatEventTime` reads that older shape literally rather
            // than letting the engine guess a timezone for it.
            render: (_: string, r: ConsignmentHistory) => (
                <span className="consignment-cell-sub">
                    {formatEventTime(r.created_at_iso ?? r.created_at)}
                </span>
            )
        },
        {
            title: 'กิจกรรม',
            dataIndex: 'action_type',
            key: 'action_type',
            width: 120,
            render: (type: string) => {
                switch (type) {
                    case 'INITIAL':
                    case 'INITIAL_DEPOSIT':
                        return <Tag className="consignment-tag active">📦 รับฝากเริ่มต้น</Tag>;
                    case 'CHECK':
                    case 'STOCK_CHECK':
                        return <Tag className="consignment-tag pending">🔍 ตรวจนับสต๊อก</Tag>;
                    case 'ADD_STOCK':
                        return <Tag color="green" className="consignment-history-action-badge">➕ ฝากเพิ่ม</Tag>;
                    case 'PERIODIC_SETTLEMENT':
                        return <Tag color="blue" className="consignment-history-action-badge">💵 จ่ายรอบนี้</Tag>;
                    case 'SETTLEMENT':
                        return <Tag className="consignment-tag settled">💰 เคลียร์ยอดเดิม</Tag>;
                    case 'RETURN':
                    case 'RETURN_TO_OWNER':
                        return <Tag className="consignment-tag returned">↩️ คืนผู้ฝาก</Tag>;
                    case 'ITEM_CLOSE':
                        return <Tag color="default">⛔ ปิดรายการสินค้า</Tag>;
                    case 'PERIOD_CLOSE':
                        return <Tag color="purple">🔒 ปิดรอบบิล</Tag>;
                    case 'CARRY_FORWARD_OUT':
                        return <Tag color="cyan">📤 ยกยอดออก</Tag>;
                    case 'CARRY_FORWARD_IN':
                        return <Tag color="cyan">📥 ยกยอดเข้า</Tag>;
                    case 'BATCH_CORRECTION':
                        return <Tag color="gold" className="consignment-history-action-badge">✏️ แก้จำนวนรับฝาก</Tag>;
                    case 'BATCH_EDIT':
                        return <Tag color="gold" className="consignment-history-action-badge">✏️ แก้ไขล็อต</Tag>;
                    case 'BATCH_VOID':
                        return <Tag className="consignment-tag-voided consignment-history-action-badge">🚫 ยกเลิกล็อต</Tag>;
                    case 'FINAL_CLOSE':
                    case 'CLOSE':
                        return <Tag color="default">🔒 ปิดรายการฝากขายทั้งหมด</Tag>;
                    default:
                        return <Tag className="consignment-tag">{type}</Tag>;
                }
            }
        },
        {
            title: 'สินค้า',
            key: 'product',
            width: 160,
            render: (_: any, r: ConsignmentHistory) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{r.product_name || 'ทั้งรอบการฝากขาย'}</div>
                    {r.product_code && <div style={{ fontSize: '11px', color: '#64748B' }}>{r.product_code}</div>}
                    {r.batch_no && <div className="consignment-cell-sub">ล็อตที่ {r.batch_no}</div>}
                </div>
            )
        },
        {
            title: 'เปลี่ยนแปลง',
            key: 'quantity_change',
            width: 90,
            align: 'right' as const,
            render: (_: any, r: ConsignmentHistory) => {
                const change = Number(r.quantity_change ?? (Number(r.new_remaining || 0) - Number(r.previous_remaining || 0)));
                return (
                    <strong style={{ color: change > 0 ? '#15803D' : change < 0 ? '#DC2626' : '#64748B' }}>
                        {change > 0 ? '+' : ''}{change}
                    </strong>
                );
            }
        },
        {
            title: 'ก่อน ➔ คงเหลือ',
            key: 'remaining_change',
            width: 110,
            render: (_: any, r: ConsignmentHistory) => (
                <span className="consignment-history-change">
                    <span className="val-old">{r.previous_remaining}</span> ➔ <span className="val-new">{r.new_remaining}</span>
                </span>
            )
        },
        {
            title: 'ขายได้',
            dataIndex: 'calculated_sold',
            key: 'calculated_sold',
            width: 80,
            align: 'right' as const,
            render: (val: number) => <span className="consignment-cell-sold" style={{ color: '#60A5FA', fontWeight: 700 }}>{val}</span>
        },
        {
            title: 'ยอดจ่ายผู้ฝาก',
            dataIndex: 'calculated_payable',
            key: 'calculated_payable',
            width: 110,
            align: 'right' as const,
            render: (val: number) => <span className="consignment-cell-payable" style={{ color: '#F87171', fontWeight: 800 }}>฿{Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
        },
        {
            title: 'กำไรร้าน',
            dataIndex: 'shop_profit',
            key: 'shop_profit',
            width: 100,
            align: 'right' as const,
            render: (val: number) => <span className="consignment-cell-profit" style={{ color: '#34D399', fontWeight: 800 }}>฿{Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
        },
        {
            // Phase 7C-6C: "ผู้บันทึก" became "ผู้ทำรายการ" -- the roadmap
            // requires both clients to use the same word for the same
            // person, and Mobile 7C-6D will use this one.
            title: 'หมายเหตุ / ผู้ทำรายการ',
            key: 'note_user',
            render: (_: any, r: ConsignmentHistory) => (
                <div>
                    <div className="consignment-history-note">{r.note || '-'}</div>
                    {/* Always rendered, never hidden when empty: a row with
                        no operator is a fact about that row, and silently
                        omitting the line made pre-authentication history
                        look the same as history nobody had checked. */}
                    <div
                        className="consignment-cell-sub"
                        data-testid="consignment-history-actor"
                        style={hasRealActor(r.recorded_by_name) ? undefined : { fontStyle: 'italic', opacity: 0.75 }}
                    >
                        โดย: {formatActor(r.recorded_by_name)}
                    </div>
                </div>
            )
        }
    ];

    return (
        <Modal
            open={open}
            title={`📜 Movement History: ${item.document_no}`}
            onCancel={onClose}
            footer={null}
            width={1050}
            destroyOnClose
        >
            <div className="consignment-history-header-box">
                ผู้ฝาก: <span style={{ fontWeight: 700 }}>{item.vendor_name}</span> | วันที่รับฝาก: <span style={{ fontWeight: 600 }}>{item.consignment_date}</span> | สินค้า: <span style={{ fontWeight: 700 }}>{item.items?.length || item.item_count || 0} รายการ</span> (รับฝากรวม {item.total_received_qty} ชิ้น)
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spin tip="กำลังโหลดประวัติ..." />
                </div>
            ) : histories.length === 0 ? (
                <Empty description="ยังไม่มีประวัติ Movement" />
            ) : (
                <Table
                    columns={columns}
                    dataSource={histories.map((h, i) => ({ ...h, key: h.id || i }))}
                    pagination={false}
                    size="small"
                    bordered
                />
            )}
        </Modal>
    );
};

export default ConsignmentHistoryModal;
